import { getIncomeYears } from 'src/pages/loan/income/data';
import type {
  BorrowerScope,
  IncomeSource,
  IncomeSourceStatus,
} from 'src/pages/loan/income/data';

/**
 * The wire shape of `income_json`: every `createForm` field, FLAT, keyed by
 * its form_json field name -- this is exactly what mos-mvp's
 * validateIncomePayload.ts checks against the tenant's form_json field list.
 * `years` (the UI-facing `Record<absoluteYear, number>`) is not itself a
 * field name; it round-trips through the `yr1`/`yr2` naming convention (see
 * AddIncomeSheet.tsx's COMMON_FIELD_NAMES comment and
 * docs/income-types-configuration-guide.md).
 */
export type LoanIncomePayloadDto = Record<string, unknown>;

/** A row as returned by GET /loans/{loanId}/incomes. */
export interface LoanIncomeDto {
  id: string;
  loanId: string;
  borrowerPairId?: string | null;
  scope?: BorrowerScope | null;
  incomeType: string;
  incomeJson: LoanIncomePayloadDto;
  analysisJson?: Record<string, unknown>;
}

/** The common columns every income type's create form shares -- typed on
 *  `IncomeSource` because the Income Summary grid sorts/totals on them.
 *  Everything else configured lands in `IncomeSource.fields`. */
const COMMON_KEYS = [
  'employer',
  'position',
  'startDate',
  'endDate',
  'payFrequency',
  'monthly',
  'ytd',
  'yr1',
  'yr2',
  'status',
  'trend',
  'supportingDocumentTypeIds',
] as const;

/** Server row -> the flat shape the grid and sheet work with. */
export const toIncomeSource = (dto: LoanIncomeDto): IncomeSource => {
  const json = dto.incomeJson ?? {};
  const [priorYear, priorYear2] = getIncomeYears();

  const years: Record<string, number> = {};
  if (json.yr1 !== undefined && json.yr1 !== null)
    years[String(priorYear)] = Number(json.yr1) || 0;
  if (json.yr2 !== undefined && json.yr2 !== null)
    years[String(priorYear2)] = Number(json.yr2) || 0;

  const fields: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(json)) {
    if (!(COMMON_KEYS as readonly string[]).includes(key)) fields[key] = value;
  }

  return {
    id: dto.id,
    type: dto.incomeType,
    borrowerPairId: dto.borrowerPairId ?? null,
    scope: dto.scope ?? null,
    employer: String(json.employer ?? ''),
    position: String(json.position ?? ''),
    startDate: String(json.startDate ?? ''),
    ...(json.endDate ? { endDate: String(json.endDate) } : {}),
    payFrequency: String(json.payFrequency ?? 'Monthly'),
    monthly: Number(json.monthly) || 0,
    ytd: Number(json.ytd) || 0,
    years,
    status: (json.status as IncomeSourceStatus) ?? 'pending',
    trend: Number(json.trend) || 0,
    supportingDocumentTypeIds: Array.isArray(json.supportingDocumentTypeIds)
      ? (json.supportingDocumentTypeIds as string[])
      : [],
    fields,
    analysisTables: (dto.analysisJson ?? {}) as IncomeSource['analysisTables'],
  };
};

/**
 * The flat UI shape -> the request body. `id`, `loanId` and the borrower
 * columns live outside income_json, so they are stripped from the payload
 * here. `years` is expanded back into `yr1`/`yr2` -- the inverse of
 * `toIncomeSource` -- and every entry in `fields` is spread in as-is.
 */
export const toLoanIncomePayload = (
  row: IncomeSource,
): LoanIncomePayloadDto => {
  const [priorYear, priorYear2] = getIncomeYears();
  return {
    employer: row.employer,
    position: row.position,
    startDate: row.startDate,
    ...(row.endDate ? { endDate: row.endDate } : {}),
    payFrequency: row.payFrequency,
    monthly: row.monthly,
    ytd: row.ytd,
    ...(row.years[String(priorYear)] !== undefined
      ? { yr1: row.years[String(priorYear)] }
      : {}),
    ...(row.years[String(priorYear2)] !== undefined
      ? { yr2: row.years[String(priorYear2)] }
      : {}),
    status: row.status,
    trend: row.trend,
    supportingDocumentTypeIds: row.supportingDocumentTypeIds,
    ...row.fields,
  };
};
